import type { LeadStatus } from '@conversia/shared-types';
import type { ConversationWithRelations } from '@/lib/types';
import { getContactDisplayName } from '@/lib/contact-display';

/**
 * Modelo de la vista CRM / Pipeline — lógica PURA y testeable. Transforma las
 * conversaciones que ya devuelve `GET /api/conversations` (con contacto, tags y
 * profileDataJson) en "leads" agrupables por temperatura, sin tocar el backend.
 * La página (`pages/crm/Crm.tsx`) sólo cablea estos helpers a react-query + DnD.
 */

export interface PipelineColumn {
  status: LeadStatus;
  label: string;
  /** Color del punto/acento de la columna (clase Tailwind de fondo). */
  dot: string;
}

/** Columnas del tablero, en orden del embudo (una por LeadStatus). */
export const PIPELINE_COLUMNS: PipelineColumn[] = [
  { status: 'CALIENTE', label: 'Caliente', dot: 'bg-red-500' },
  { status: 'TIBIO', label: 'Tibio', dot: 'bg-amber-500' },
  { status: 'FRIO', label: 'Frío', dot: 'bg-sky-500' },
  { status: 'LARGO_PLAZO', label: 'Largo plazo', dot: 'bg-violet-500' },
  { status: 'CERRADO', label: 'Cerrado', dot: 'bg-emerald-500' },
  { status: 'NO_CERRADO', label: 'No cerrado', dot: 'bg-slate-400' },
];

export interface LeadTag {
  id: string;
  name: string;
  color: string;
}

/** Vista de un lead en el tablero (derivada de una conversación + su contacto). */
export interface Lead {
  /** Id de la conversación: la unidad que lleva el leadStatus y a la que se navega. */
  conversationId: string;
  contactId: string;
  name: string;
  phone: string | null;
  modelo: string | null;
  presupuesto: string | null;
  zona: string | null;
  tipoPuerta: string | null;
  lastContactAt: string | null;
  leadStatus: LeadStatus;
  tags: LeadTag[];
}

/** Lee un string no vacío (o número) de profileDataJson. */
function readField(profile: Record<string, unknown>, key: string): string | null {
  const v = profile[key];
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  return null;
}

/**
 * Convierte una conversación en un Lead. Devuelve `null` si la conversación aún
 * no tiene temperatura (`leadStatus` nulo): el tablero sólo muestra leads ya
 * clasificados, una columna por estado.
 */
export function leadFromConversation(c: ConversationWithRelations): Lead | null {
  if (!c.leadStatus) return null;
  const contact = c.contact ?? null;
  const profile = (contact?.profileDataJson ?? {}) as Record<string, unknown>;
  return {
    conversationId: c.id,
    contactId: contact?.id ?? c.contactId,
    name: getContactDisplayName(contact),
    phone: contact?.phone ?? null,
    modelo: readField(profile, 'modelo_interes'),
    presupuesto: readField(profile, 'presupuesto'),
    zona: readField(profile, 'zona'),
    tipoPuerta: readField(profile, 'material_puerta'),
    lastContactAt: c.lastMessageAt ? String(c.lastMessageAt) : null,
    leadStatus: c.leadStatus,
    tags: (c.tags ?? []).map((t) => ({ id: t.tag.id, name: t.tag.name, color: t.tag.color })),
  };
}

export interface LeadFilters {
  /** Filtra por etiqueta (id). */
  tagId?: string;
  /** Filtra por substring del modelo de interés (case-insensitive). */
  modelo?: string;
}

/** Predicado de filtros del lado del cliente (etiqueta + modelo de interés). */
export function matchesLeadFilters(lead: Lead, f: LeadFilters): boolean {
  if (f.tagId && !lead.tags.some((t) => t.id === f.tagId)) return false;
  if (f.modelo && !(lead.modelo ?? '').toLowerCase().includes(f.modelo.toLowerCase())) {
    return false;
  }
  return true;
}

export type LeadGroups = Record<LeadStatus, Lead[]>;

export function emptyGroups(): LeadGroups {
  return { CALIENTE: [], TIBIO: [], FRIO: [], LARGO_PLAZO: [], CERRADO: [], NO_CERRADO: [] };
}

/** Agrupa leads por temperatura (una lista por columna del tablero). */
export function groupLeadsByStatus(leads: Lead[]): LeadGroups {
  const groups = emptyGroups();
  for (const lead of leads) groups[lead.leadStatus].push(lead);
  return groups;
}

/**
 * Pipeline completo: conversaciones → leads (clasificados) → filtrados → agrupados.
 * Punto único que usa la página para pintar el tablero.
 */
export function buildPipeline(
  items: ConversationWithRelations[],
  filters: LeadFilters = {},
): LeadGroups {
  const leads = items
    .map(leadFromConversation)
    .filter((l): l is Lead => l !== null)
    .filter((l) => matchesLeadFilters(l, filters));
  return groupLeadsByStatus(leads);
}

// --------------------------- Vista de tabla (F5) ---------------------------

export type TableSortKey = 'name' | 'leadStatus' | 'lastContactAt' | 'modelo';

export interface TableSort {
  key: TableSortKey;
  dir: 'asc' | 'desc';
}

/** Posición de cada temperatura en el embudo (para ordenar por estado). */
const FUNNEL_ORDER: Record<LeadStatus, number> = Object.fromEntries(
  PIPELINE_COLUMNS.map((c, i) => [c.status, i]),
) as Record<LeadStatus, number>;

function compareLeads(a: Lead, b: Lead, sort: TableSort): number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  switch (sort.key) {
    case 'name':
      return a.name.localeCompare(b.name, 'es') * dir;
    case 'leadStatus':
      return (FUNNEL_ORDER[a.leadStatus] - FUNNEL_ORDER[b.leadStatus]) * dir;
    case 'modelo': {
      // Nulos SIEMPRE al final, en cualquier dirección.
      if (a.modelo == null && b.modelo == null) return 0;
      if (a.modelo == null) return 1;
      if (b.modelo == null) return -1;
      return a.modelo.localeCompare(b.modelo, 'es') * dir;
    }
    case 'lastContactAt': {
      if (a.lastContactAt == null && b.lastContactAt == null) return 0;
      if (a.lastContactAt == null) return 1;
      if (b.lastContactAt == null) return -1;
      return (Date.parse(a.lastContactAt) - Date.parse(b.lastContactAt)) * dir;
    }
  }
}

/**
 * Filas de la vista de tabla del CRM: mismos leads y filtros que el tablero,
 * aplanados. Sin `sort`, el orden es el del embudo (columna por columna).
 */
export function buildTableRows(
  items: ConversationWithRelations[],
  filters: LeadFilters = {},
  sort?: TableSort,
): Lead[] {
  const groups = buildPipeline(items, filters);
  const rows = PIPELINE_COLUMNS.flatMap((col) => groups[col.status]);
  if (!sort) return rows;
  return [...rows].sort((a, b) => compareLeads(a, b, sort));
}

/**
 * Devuelve una copia de la lista de conversaciones con el `leadStatus` de UNA
 * cambiado. Es el movimiento OPTIMISTA del drag&drop: mueve la card de columna en
 * la cache antes de que responda el PATCH (rollback si falla).
 */
export function withLeadStatus(
  items: ConversationWithRelations[],
  conversationId: string,
  leadStatus: LeadStatus,
): ConversationWithRelations[] {
  return items.map((c) => (c.id === conversationId ? { ...c, leadStatus } : c));
}
